import {
  startOfDay,
  addDays,
  subDays,
  isAfter,
  isBefore,
  isSameDay,
} from "date-fns";
import { getItemsFromCollection } from "directus/src/operations";
import { useQuery } from "@tanstack/react-query";
import { formatDateForAPI } from "utils/utils";

const calculateDateStats = (billables, currencies, statsType) => {
  const today = startOfDay(new Date());

  // Helper function to convert amount to INR
  const convertToINR = (amount, currencyId) => {
    if (!amount || !currencyId) return 0;

    const currency = currencies.find((c) => c.id === currencyId);
    if (!currency) return Number(amount);

    return Number(amount) * Number(currency.budget_rate_inr || 1);
  };

  // Due date based calculations (for billed status)
  const calculateDueStats = () => {
    const getDueToday = () => {
      return billables
        .filter((b) => {
          const dueDate = new Date(b.due_date);
          return isSameDay(dueDate, today);
        })
        .reduce((sum, b) => {
          const convertedAmount = convertToINR(b.adjusted_amount, b.currency);
          return sum + convertedAmount;
        }, 0);
    };

    const getDueInDays = (days) => {
      const startDate = addDays(today, 1); // Start from tomorrow
      const endDate = addDays(today, days); // End at N days from today

      return billables
        .filter((b) => {
          const dueDate = new Date(b.due_date);
          return (
            (isAfter(dueDate, today) || isSameDay(dueDate, startDate)) &&
            (isBefore(dueDate, endDate) || isSameDay(dueDate, endDate))
          );
        })
        .reduce((sum, b) => {
          const convertedAmount = convertToINR(b.adjusted_amount, b.currency);
          return sum + convertedAmount;
        }, 0);
    };

    return {
      dueToday: getDueToday(),
      dueNext15Days: getDueInDays(15),
      dueNext30Days: getDueInDays(30),
    };
  };

  // Collection date based calculations (for collected status)
  const calculateCollectedStats = () => {
    const getCollectedInLastDays = (days) => {
      const pastDate = subDays(today, days);
      return billables
        .filter((b) => {
          const collectedDate = new Date(b.collected_date);
          return (
            isAfter(collectedDate, pastDate) &&
            (isBefore(collectedDate, today) || isSameDay(collectedDate, today))
          );
        })
        .reduce((sum, b) => {
          const convertedAmount = convertToINR(b.adjusted_amount, b.currency);
          return sum + convertedAmount;
        }, 0);
    };

    return {
      collectedLast15Days: getCollectedInLastDays(15),
      collectedLast30Days: getCollectedInLastDays(30),
    };
  };

  // Return stats based on type
  switch (statsType) {
    case "due":
      return calculateDueStats();
    case "collected":
      return calculateCollectedStats();
    default:
      return {};
  }
};

// Configuration for different stat types
const STATS_CONFIG = {
  due: {
    status: "billed",
    dateField: "due_date",
    requiredFields: ["adjusted_amount", "currency", "due_date"],
    getDateFilter: () => {
      const today = startOfDay(new Date());
      const future30Days = addDays(today, 30);
      return {
        due_date: {
          _gte: formatDateForAPI(today),
          _lte: formatDateForAPI(future30Days),
        },
      };
    },
  },
  collected: {
    status: "collected",
    dateField: "collected_date",
    requiredFields: ["adjusted_amount", "currency", "collected_date"],
    getDateFilter: () => {
      const today = startOfDay(new Date());
      const past30Days = subDays(today, 30);
      return {
        collected_date: {
          _gte: formatDateForAPI(past30Days),
          _lte: formatDateForAPI(today),
        },
      };
    },
  },
};

export const useDateBasedStats = (statsType, filters,status) => {
  const config = STATS_CONFIG[statsType];

  const dynamicFilters = {

    ...Object.entries(filters ?? {}).reduce((acc, [key, value]) => {
      if (["invoice_date", "due_date", "billing_date", "collected_date"].includes(key)) {
        return acc;
      }
      acc[key] = value;
      return acc;
    }, {}),
  };

  return useQuery({
    queryKey: ["date-based-stats", statsType, dynamicFilters],
    queryFn: async () => {
      if (!config) {
        throw new Error(`Invalid stats type: ${statsType}`);
      }

      // Fetch billables data based on configuration
      const billablesQuery = {
        fields: config.requiredFields,
        filter: {
          status: { _eq: config.status },
          ...config.getDateFilter(), // Use dynamic date filter
          ...dynamicFilters,
        },
        limit: -1,
      };

      const billablesResponse = await getItemsFromCollection(
        "billables",
        billablesQuery
      );

      // Fetch currencies data
      const currenciesQuery = {
        limit: -1,
      };
      const currenciesResponse = await getItemsFromCollection(
        "currencies",
        currenciesQuery
      );

      return calculateDateStats(
        billablesResponse,
        currenciesResponse,
        statsType
      );
    },
    // Only run query if statsType is valid
    enabled: !!config,
  });
};

// Convenience hooks for specific use cases
export const useDueStats = (filters,status) => {
  return useDateBasedStats("due", filters,status);
};

export const useCollectedStats = (filters,status) => {
  return useDateBasedStats("collected", filters,status);
};
